/**
 * Centralized enums for cadences-v2.
 * Use these instead of magic strings for cadence types, statuses, event types, etc.
 */

/** Cadence type — always underscore, matching the GraphQL schema. */
export const CADENCE_TYPE = {
  CADENCE: 'cadence',
  PROSPECT_PLAN: 'prospect_plan',
  BULK_MESSAGE: 'bulk_message',
};

/** Cadence lifecycle status. */
export const CADENCE_STATUS = {
  ACTIVE: 'active',
  DRAFT: 'draft',
  PAUSED: 'paused',
  ARCHIVED: 'archived',
};

/** Step/event types — always lowercase for frontend comparisons. */
export const EVENT_TYPE = {
  EMAIL: 'email',
  WAIT: 'wait',
  CONDITION: 'condition',
  TASK: 'task',
  START: 'start',
  END: 'end',
  EXIT: 'exit',
  SMS: 'sms',
  CALL: 'call',
  MANUAL: 'manual',
};

/** Edge types — always uppercase, matching the GraphQL schema. */
export const EDGE_TYPE = {
  NEXT: 'NEXT',
  SUCCESS: 'SUCCESS',
  FAILURE: 'FAILURE',
};

/** Display labels for cadence types. */
export const CADENCE_TYPE_LABELS = {
  [CADENCE_TYPE.CADENCE]: 'Cadence',
  [CADENCE_TYPE.PROSPECT_PLAN]: 'Prospect Plan',
  [CADENCE_TYPE.BULK_MESSAGE]: 'Bulk Message',
};

/**
 * Normalize event type to lowercase for comparison.
 * Backend GraphQL sends uppercase ('EMAIL'), frontend uses lowercase ('email').
 */
export const normalizeEventType = (type) => (type || '').toLowerCase();

/**
 * Check if a cadence type string matches BULK_MESSAGE.
 * Handles the historical inconsistency between 'bulk_message' (DB) and 'bulk-message' (URLs).
 */
export const isBulkMessageType = (type) => {
  const t = (type || '').toLowerCase();
  return t === CADENCE_TYPE.BULK_MESSAGE || t === 'bulk-message';
};

/**
 * Check if a cadence type string matches PROSPECT_PLAN.
 * Handles the historical inconsistency between 'prospect_plan' (DB) and 'prospect-plan' (URLs).
 */
export const isProspectPlanType = (type) => {
  const t = (type || '').toLowerCase();
  return t === CADENCE_TYPE.PROSPECT_PLAN || t === 'prospect-plan';
};

/** Human-readable labels keyed by displayStatus. Mirrors backend DISPLAY_STATUS_LABELS. */
export const DISPLAY_STATUS_LABELS = {
  enrolled: 'Enrolled',
  in_progress: 'In Progress',
  paused: 'Paused',
  completed: 'Completed',
  early_exit_manual: 'Early Exit: Manual Removal',
  early_exit_failed_send: 'Early Exit: Failed Send',
  early_exit_skipped_send: 'Early Exit: Skipped Send',
  early_exit_auto_exit: 'Early Exit: Auto Exit Event',
  early_exit_configured: 'Early Exit: Configured Exit Event',
  n_a: 'N/A',
  delivered: 'Delivered',
  pending: 'Pending',
  send_scheduled: 'Send Scheduled',
  failed_send: 'Failed Send',
  skipped: 'Skipped',
  unsubscribed: 'Unsubscribed',
  opened: 'Opened',
  clicked: 'Clicked',
  not_applicable: 'N/A',
  blocked: 'Blocked',
};

const _SKIP_TOOLTIP_TITLES = {
  missing_contact_info: 'Skipped — Missing Email Address',
  no_email: 'Skipped — Missing Email Address',
  missing_merge_field: 'Skipped — Missing Merge Field',
  missing_merge_fields: 'Skipped — Missing Merge Field',
  unsubscribed: 'Skipped — Unsubscribed',
  historic_hard_bounce: 'Skipped — Historic Hard Bounce',
};

const _FAIL_TOOLTIP_TITLES = {
  hard_bounce: 'Bounce — Unknown User',
  soft_bounce: 'Bounce — Org Rejected',
  spam_block: 'Spam Block',
  dmarc_policy: 'Bounce — DMARC Policy',
  dns_error: 'Bounce — DNS Error',
  blocked: 'Bounce — ISP Blocked',
  blocked_sender: 'Bounce — Sender Blocked',
};

/** True when displayStatus reflects an early-exit cadence outcome. */
export const isEarlyExit = (displayStatus) => (displayStatus || '').startsWith('early_exit');

/** True when the member is actively enrolled and progressing. */
export const isActiveMember = (displayStatus) => displayStatus === 'in_progress' || displayStatus === 'enrolled';

/** Resolve a human-readable status label from a displayStatus key or member object. */
export const getDisplayStatusLabel = (obj) => {
  if (!obj) return 'Unknown';
  const key = typeof obj === 'string' ? obj : obj.displayStatus;
  return (
    DISPLAY_STATUS_LABELS[key] || (key ? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Unknown')
  );
};

/**
 * Resolve the short tooltip title for skip/fail statuses, or null.
 * Uses displayStatus to determine if a title is needed, then removalReason for the specific title.
 * Falls back to failureEvents[0].failureType if removalReason has no match.
 */
export const getStatusTooltipTitle = (member) => {
  if (!member) return null;
  const ds = member.displayStatus || '';
  const reason = (member.removalReason || '').toLowerCase();

  if (ds === 'skipped' || ds === 'early_exit_skipped' || ds === 'early_exit_skipped_send') {
    if (_SKIP_TOOLTIP_TITLES[reason]) return _SKIP_TOOLTIP_TITLES[reason];
    const fe = (member.failureEvents || [])[0];
    const ft = fe ? (fe.failureType || '').toLowerCase() : '';
    return _SKIP_TOOLTIP_TITLES[ft] || 'Skipped';
  }

  if (ds === 'failed_send' || ds === 'early_exit_failed_send') {
    if (_FAIL_TOOLTIP_TITLES[reason]) return _FAIL_TOOLTIP_TITLES[reason];
    const fe = (member.failureEvents || [])[0];
    const ft = fe ? (fe.failureType || '').toLowerCase() : '';
    return _FAIL_TOOLTIP_TITLES[ft] || 'Failed Send';
  }

  if (ds === 'blocked') return 'Processing Error';

  return null;
};
